import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuthStore } from '@/store/auth-store'
import { authService } from '@/services/auth-service'
import { LoadingSpinner } from '@/components/common/loading-spinner'

const MOBILE_BANNER_KEY = 'paver_mobile_banner_dismissed'

function isMobileDevice() {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
}

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showMobileBanner] = useState(
    () => isMobileDevice() && localStorage.getItem(MOBILE_BANNER_KEY) !== '1'
  )
  const [bannerDismissed, setBannerDismissed] = useState(false)

  function dismissBanner() {
    localStorage.setItem(MOBILE_BANNER_KEY, '1')
    setBannerDismissed(true)
  }

  const { login } = useAuthStore()
  const navigate = useNavigate()
  // const location = useLocation()
  // const _from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await authService.login({ email, password })
      login(result.user, result.token)
      navigate('/dashboard', { replace: true })
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data
      setError(errData?.message || errData?.error || 'Invalid credentials. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 relative overflow-hidden">
        <div className="login-glow">
          <div className="login-glow-inner" />
          <div className="login-glow-inner2" />
          <div className="login-glow-dots" />
        </div>
        <div className="relative z-10">
          <img src="/images/paver-logo.png" alt="Paver" className="mx-auto h-32" />
        </div>
      </div>

      {/* Right panel - login form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center p-6 sm:p-12 relative">
        {/* Mobile app nudge banner */}
        {showMobileBanner && !bannerDismissed && (
          <div className="login-mobile-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
            <span>
              On your phone?{' '}
              <a href="https://m.getpaver.io" target="_blank" rel="noopener noreferrer">
                The Paver Field App is faster →
              </a>
            </span>
            <button onClick={dismissBanner} aria-label="Dismiss" className="login-mobile-banner-close">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}
        <Card className="w-full max-w-md border-0 shadow-none sm:border sm:shadow-sm">
          <CardHeader className="space-y-1 text-center">
            {/* Mobile logo */}
            <img src="/images/paver-logo.png" alt="Paver" className="mx-auto mb-4 h-12 lg:hidden" />
            <CardTitle className="text-2xl font-bold">Sign in</CardTitle>
            <CardDescription>Enter your credentials to access your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="login-btn" disabled={loading}>
                {loading ? (
                  <LoadingSpinner size="sm" className="text-primary-foreground" />
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Legal footer links */}
        <div className="login-legal-footer">
          <Link to="/privacy">Privacy Policy</Link>
          <span className="login-legal-sep">|</span>
          <Link to="/terms">Terms &amp; Conditions</Link>
        </div>
      </div>
    </div>
  )
}
